import { ProductError } from "@b2bportal/purchase-api";
import {
  CDN_ASSETS,
  DO_NOT_APPLY_REWARDS_KEY,
  getCurrentCorpAccount,
  getConfigTenant,
  getIsEMEligible,
  isCorpTenant,
  isVesperTenant,
  useShowPolicyBanner,
} from "@capone/common";
import { useExperimentIsVariant } from "@capone/experiments";
import { Box, Button, Divider, Typography } from "@material-ui/core";
import clsx from "clsx";
import {
  ActionButton,
  ActionLink,
  ApprovalBanner,
  ApprovalReasonTextarea,
  BannerSeverity,
  ButtonWrap,
  CheckoutStepper,
  ContactInfoForm,
  CorpRewardsEarnBanner,
  DesktopPopupModal,
  emailRegex,
  FunnelType,
  Icon,
  IconName,
  IContactInfo,
  NotificationBanner,
  phoneRegex,
  PolicyBanner,
  frequentFlyerRegex as rewardsRegex,
  InformationalModal as TreesModal,
  TripPurposePanel,
  TripDescription,
} from "halifax";
import React, { useCallback, useContext, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { RouteComponentProps } from "react-router";
import {
  ADD_CONTACT_INFO,
  CAR_SAVE_AND_CONTINUE_CLICKED,
  CarsBookErrorEnum,
  ITravelerStepErrors,
  PaymentError,
  IPerson,
} from "redmond";

import { AGENT_FEE, CarBookSummaryPanel } from "../..";
import { trackEvent } from "../../../../../api/v1/analytics/trackEvent";
import { fetchCustomerDetails } from "../../../../../api/v1/customer/fetchCustomerDetails";
import { ClientContext } from "../../../../../App";
import {
  AVAILABLE,
  CHECKOUT_REWARDS_EARN_BANNER_EXPERIMENT,
  CORP_FTB_OFFER_ADDITIONAL_PLACEMENTS,
  CREDIT_OFFER_STACKING_V1,
  getExperimentVariant,
  TRAVEL_CREDIT_HISTORY_EXPERIMENT,
  TRAVEL_WALLET_CREDITS_EXPERIMENT,
  TREES_MODAL_EXPERIMENT,
  useExperiments,
  CARS_FLIGHT_NUMBER_INPUT,
} from "../../../../../context/experiments";
import { PATH_BOOK_CONFIRMATION, PATH_HOME } from "../../../../../utils/paths";
import { getRewardsAccounts } from "../../../../rewards/reducer/selectors";
import { CarShopDetails } from "../../../../shop/components/CarShopDetails";
import { PaymentCard } from "../../PaymentCard";
import { PriceBreakdown } from "../../PriceBreakdownCard";
import { TravelOfferSelection } from "../../TravelOfferSelection";
import { CarBookPassengerSelection } from "../CarBookPassengerSelection";
import { DesktopCarBookWorkflowConnectorProps } from "./container";
import { FlightNumberForm } from "../../FlightNumberForm";
import "./styles.scss";
import dayjs from "dayjs";
import * as t from "./textConstants";

export interface IDesktopCarBookWorkflowProps
  extends RouteComponentProps,
    DesktopCarBookWorkflowConnectorProps {}

export const CorpDesktopCarBookWorkflow = ({
  setContactInfo,
  driverAge,
  history,
  combinedBookingSteps,
  schedulePriceQuote,
  isBookingValid,
  isContactStepComplete,
  isTravelerStepComplete,
  priceQuote,
  priceDifferenceAcknowledged,
  confirmation,
  scheduleBook,
  allStepsCompleted,
  email,
  phoneNumber,
  setSelectedPaymentMethodId,
  setSelectedRewardsAccountReferenceId,
  priceQuoteErrors,
  setPriceQuote,
  isHertzProvider,
  hertzLoyaltyRewardsNumber,
  confirmationDetailsErrors,
  selectedVehicle,
  fetchApplicableTravelWalletItems,
  isTravelWalletPaymentOnly,
  fetchTravelWalletCreditHistory,
  fetchTravelWalletDetails,
  useV1PurchaseFlow,
  setTripPurpose,
  setTripDescription,
  largestValueAccount,
  calculatedEarn,
  fetchProductToEarn,
  selectedTravelerId,
  isMissingLicense,
  driver,
  updateUserPassenger,
  setPendingLoyaltySync,
  hasFlightInfoField,
  isFlightInfoValid,
}: IDesktopCarBookWorkflowProps): JSX.Element => {
  const corporateTravel = selectedVehicle?.corporateTravel;
  const isInPolicy =
    // TODO(replat): add back after BE work is done
    //   priceQuote
    // ? (priceQuote.vehAvail as CorpVehicleAvail).corporateTravel.policyCompliance
    //     .isInPolicy
    // :
    corporateTravel?.policyCompliance.isInPolicy ?? true;
  const isVesper = isVesperTenant(getConfigTenant());

  const [approvalRequestReason, setApprovalRequestReason] = useState<
    string | undefined
  >();

  const { policies } = useContext(ClientContext);

  const maxPricePerDay =
    policies?.cars.policies[0]?.maxPricePerDay ?? undefined;

  const onBookCar = () => {
    priceDifferenceAcknowledged || !!priceQuote
      ? scheduleBook({
          agentFee: isAgentPortal ? AGENT_FEE : 0,
          approvalRequestReason: approvalRequestReason,
          initialTime: dayjs().toISOString(),
        })
      : schedulePriceQuote(history, isAgentPortal ? AGENT_FEE : 0, undefined);
  };

  const handleReasonChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>, isCharLimitError: boolean) => {
      setApprovalRequestReason(e.target.value);
      if (isCharLimitError) {
        trackEvent({
          eventName: "approval_reason_char_limit_error",
          properties: {
            funnel: "cars",
          },
        });
      }
    },
    []
  );

  const expState = useExperiments();

  const isFlightNumberInputEnabled =
    getExperimentVariant(expState.experiments, CARS_FLIGHT_NUMBER_INPUT) ===
    AVAILABLE;
  const showFlightNumberForm = isFlightNumberInputEnabled && hasFlightInfoField;

  const treesModalExperiment = getExperimentVariant(
    expState.experiments,
    TREES_MODAL_EXPERIMENT
  );
  const isTreesModalExperiment = useMemo(
    () => treesModalExperiment === AVAILABLE,
    [treesModalExperiment]
  );

  const isTravelWalletCreditsExperiment = useExperimentIsVariant(
    TRAVEL_WALLET_CREDITS_EXPERIMENT,
    AVAILABLE
  );

  const isTravelCreditHistoryExperiment = useExperimentIsVariant(
    TRAVEL_CREDIT_HISTORY_EXPERIMENT,
    AVAILABLE
  );

  const isCreditAndOfferStackingExperimentV1 = useExperimentIsVariant(
    CREDIT_OFFER_STACKING_V1,
    AVAILABLE
  );

  const isTripPurposeXpEnabled = useExperimentIsVariant(
    "corp-trip-purpose",
    AVAILABLE
  );

  const isExpenseManagementExperiment = useExperimentIsVariant(
    "corp-expense-management",
    AVAILABLE
  );

  const isCheckoutRewardsEarnBannerExperiment = useExperimentIsVariant(
    CHECKOUT_REWARDS_EARN_BANNER_EXPERIMENT,
    AVAILABLE
  );

  const isFtbOfferAdditionalPlacementsEnabled =
    getExperimentVariant(
      expState.experiments,
      CORP_FTB_OFFER_ADDITIONAL_PLACEMENTS
    ) === AVAILABLE;

  const clientContext = useContext(ClientContext);
  const { sessionInfo, isAgentPortal } = clientContext;

  const rewardsAccounts = useSelector(getRewardsAccounts);
  const config = useSelector((state: any) => state.config);
  const accountForBanner = useMemo(() => {
    if (isCorpTenant(config?.TENANT)) {
      const corpAccount = getCurrentCorpAccount(rewardsAccounts, sessionInfo);
      return corpAccount || largestValueAccount;
    }
    return largestValueAccount;
  }, [rewardsAccounts, sessionInfo, largestValueAccount, config?.TENANT]);
  const isEMEligible = getIsEMEligible(accountForBanner);
  const isFirstStepCompletedInCheckout =
    isContactStepComplete && isTravelerStepComplete && selectedTravelerId !== undefined && !isMissingLicense;
  const [showErrors, setShowErrors] = useState<ITravelerStepErrors>({
    phone: false,
    email: false,
    travelerSelect: false,
    loyaltyNumber: false,
  });

  const [contactInfo, setContact] = useState<IContactInfo | null>({
    phoneNumber: phoneNumber || "",
    email: email || sessionInfo?.userInfo?.email || "",
  });
  const [customerDetailsLoading, setCustomerDetailsLoading] =
    React.useState<boolean>(true);
  const [disableEditTravelerInfo, setDisableEditTravelerInfo] = useState(false);
  const [groundDetailsModalOpen, setGroundDetailsModalOpen] = useState(false);
  const [treeModalOpen, setTreeModalOpen] = useState(false);

  const showPolicyBanner = useShowPolicyBanner(policies, "car");

  React.useEffect(() => {
    if (confirmation) {
      history.push(PATH_BOOK_CONFIRMATION);
    }
  }, [confirmation]);

  React.useEffect(() => {
    if (priceQuoteErrors.length > 0 || confirmationDetailsErrors.length > 0) {
      setDisableEditTravelerInfo(false);
    }
  }, [priceQuoteErrors, confirmationDetailsErrors]);

  React.useEffect(() => {
    if (priceQuoteErrors.length > 0) {
      setDisableEditTravelerInfo(false);
    }
  }, [priceQuoteErrors]);

  React.useEffect(() => {
    if (
      confirmationDetailsErrors.some((error) =>
        useV1PurchaseFlow
          ? (error as ProductError)?.value?.value?.msg ===
            CarsBookErrorEnum.LoyaltyNameMismatch
          : (error as PaymentError)?.msg ===
            CarsBookErrorEnum.LoyaltyNameMismatch
      )
    ) {
      setDisableEditTravelerInfo(false);
    }
  }, [confirmationDetailsErrors]);

  React.useEffect(() => {
    const getCustomerDetails = async () => {
      try {
        const details = await fetchCustomerDetails();
        setContact({
          email: "",
          ...contactInfo,
          phoneNumber: details?.phoneNumber || "",
        });
        setContactInfo(contactInfo?.email || "", details?.phoneNumber || "");
      } finally {
        setCustomerDetailsLoading(false);
      }
    };

    getCustomerDetails();
    isTravelWalletCreditsExperiment && fetchApplicableTravelWalletItems();
    isTravelCreditHistoryExperiment && fetchTravelWalletCreditHistory();
    if (isFtbOfferAdditionalPlacementsEnabled) fetchTravelWalletDetails();
  }, []);

  React.useEffect(() => {
    if (largestValueAccount) {
      fetchProductToEarn();
    }
  }, [largestValueAccount, fetchProductToEarn]);

  const isUnderAge = (driverAge || 0) < 25;

  const renderLogo = (
    <ButtonWrap
      className="logo"
      onClick={() => {
        history.push(PATH_HOME);
      }}
    >
      {clientContext.logo}
    </ButtonWrap>
  );

  return (
    <Box
      className={clsx("corp-desktop-car-book-workflow-root", "home-screen-redesign", {
        redesigned: isVesper,
      })}
    >
      <CheckoutStepper
        steps={combinedBookingSteps}
        headerCopy={t.BOOK_BUTTON_TEXT}
        subHeaderCopy={t.REQUIRED_DETAILS_TEXT}
        logo={renderLogo}
        className={clsx("b2b", "combined-step")}
      />
      <Box className="desktop-car-book-workflow-container">
        <Box className={clsx("checkout-template-column", "left")}>
          <Box
            className={clsx("checkout-template-card-content-container", "car")}
          >
            <CarBookSummaryPanel
              hideButtons
              showPolicyBanner={showPolicyBanner}
            />
          </Box>

          {isTripPurposeXpEnabled && !isExpenseManagementExperiment && (
            <Box
              className={clsx(
                "checkout-template-card-content-container",
                "trip-purpose"
              )}
            >
              <TripPurposePanel setTripPurpose={setTripPurpose} />
            </Box>
          )}

          {isTravelWalletCreditsExperiment &&
            !isCreditAndOfferStackingExperimentV1 && <TravelOfferSelection />}
          <Box className="checkout-template-card-content-container traveler">
            <Box className={clsx("checkout-template-description")}>
              <Typography
                variant="h2"
                className={clsx("checkout-template-title")}
              >
                {t.TRAVELER_INFO_TITLE}
              </Typography>
            </Box>
            {!isVesper && <Divider className="traveler-info-container-divider" />}
            <CarBookPassengerSelection
              disableEditTravelerInfo={disableEditTravelerInfo}
              setProgress={() => undefined}
              showErrors={showErrors}
              setShowErrors={setShowErrors}
            />
            {!isVesper && <Divider className="traveler-info-container-divider" />}
            <ContactInfoForm
              disabled={disableEditTravelerInfo}
              title={t.CONTACT_INFO_TITLE_NO_STEP}
              subtitle={t.CONTACT_INFO_SUBTITLE}
              contactInfo={contactInfo}
              onContactInfoChange={(info: IContactInfo) => {
                setContact(info);
                setContactInfo(info.email, info.phoneNumber);
                if (
                  info.email &&
                  info.phoneNumber &&
                  emailRegex.test(info.email) &&
                  phoneRegex.test(info.phoneNumber)
                ) {
                  trackEvent({
                    eventName: ADD_CONTACT_INFO,
                    properties: {},
                  });
                }
              }}
              showErrors={showErrors}
              setShowErrors={setShowErrors}
              loading={customerDetailsLoading}
            />

            {showFlightNumberForm && (
              <>
                <Divider className="flight-number-form-divider" />
                <FlightNumberForm disabled={disableEditTravelerInfo} />
              </>
            )}

            {!disableEditTravelerInfo && (
              <ActionButton
                className={clsx(
                  "traveler-select-workflow-button",
                  "traveler-step-cta"
                )}
                message="Save & continue"
                onClick={() => {
                  const hasValidLoyaltyNumber =
                    isHertzProvider && hertzLoyaltyRewardsNumber
                      ? rewardsRegex.test(hertzLoyaltyRewardsNumber)
                      : true;

                  const hasValidFlightInfo = showFlightNumberForm
                    ? isFlightInfoValid
                    : true;

                  if (
                    isFirstStepCompletedInCheckout &&
                    hasValidLoyaltyNumber &&
                    hasValidFlightInfo
                  ) {
                    if (driver && hertzLoyaltyRewardsNumber) {
                      const HERTZ_VENDOR_CODE = "ZE";
                      const existingLoyalty = driver.groundLoyalty?.[HERTZ_VENDOR_CODE]?.value;
                      if (existingLoyalty !== hertzLoyaltyRewardsNumber) {
                        const updatedDriver: IPerson = {
                          ...driver,
                          groundLoyalty: {
                            ...driver.groundLoyalty,
                            [HERTZ_VENDOR_CODE]: { value: hertzLoyaltyRewardsNumber },
                          },
                        };
                        setPendingLoyaltySync({
                          vendorCode: HERTZ_VENDOR_CODE,
                          loyaltyNumber: hertzLoyaltyRewardsNumber,
                        });
                        updateUserPassenger({
                          person: updatedDriver,
                          isNewTraveler: false,
                          source: "cars_checkout",
                        });
                      }
                    }
                    schedulePriceQuote(
                      history,
                      isAgentPortal ? AGENT_FEE : 0,
                      true
                    );
                    setDisableEditTravelerInfo(true);
                    setShowErrors({
                      phone: false,
                      email: false,
                      travelerSelect: false,
                      loyaltyNumber: false,
                      corpPrimaryTraveler: false,
                    });
                    trackEvent({
                      eventName: CAR_SAVE_AND_CONTINUE_CLICKED,
                      properties: {
                        has_form_errors: false,
                      },
                    });
                  } else {
                    setShowErrors({
                      phone:
                        !contactInfo ||
                        !phoneRegex.test(contactInfo.phoneNumber),
                      email:
                        !contactInfo || !emailRegex.test(contactInfo.email),
                      travelerSelect: !isTravelerStepComplete || selectedTravelerId === undefined,
                      corpPrimaryTraveler: !isTravelerStepComplete || selectedTravelerId === undefined,
                      loyaltyNumber: !hasValidLoyaltyNumber,
                    });
                    trackEvent({
                      eventName: CAR_SAVE_AND_CONTINUE_CLICKED,
                      properties: {
                        has_form_errors: true,
                        traveler_error: !isTravelerStepComplete,
                        email_error:
                          !contactInfo || !emailRegex.test(contactInfo.email),
                        phone_error:
                          !contactInfo ||
                          !phoneRegex.test(contactInfo.phoneNumber),
                        loyaltyNumber: !hasValidLoyaltyNumber,
                      },
                    });
                  }
                }}
                defaultStyle="h4r-primary"
              />
            )}
            {disableEditTravelerInfo && (
              <ActionLink
                className={clsx("traveler-step-cta", "b2b")}
                content={
                  <Box className="edit-traveler-link">
                    <Typography variant="body1">
                      Edit traveler information
                    </Typography>
                    <Icon
                      aria-hidden
                      className="icon-end"
                      name={IconName.B2BEditPencil}
                      ariaLabel=""
                    />
                  </Box>
                }
                onClick={() => {
                  setPriceQuote(null);
                  setDisableEditTravelerInfo(false);
                  setSelectedPaymentMethodId({
                    paymentMethodId: "",
                    accountId: undefined,
                  });
                  setSelectedRewardsAccountReferenceId(
                    DO_NOT_APPLY_REWARDS_KEY
                  );
                }}
              />
            )}
            <Box className={clsx("contact-info-helper-container")}>
              <Typography
                variant="body2"
                className={clsx("contact-info-helper-text")}
              >
                {t.CONTACT_INFO_HELPER_TEXT}
              </Typography>
            </Box>
          </Box>
          <Box
            className={clsx(
              "checkout-template-card-content-container",
              "credit"
            )}
          >
            <PaymentCard
              disabled={
                !isFirstStepCompletedInCheckout ||
                !disableEditTravelerInfo ||
                (!isCreditAndOfferStackingExperimentV1 &&
                  isTravelWalletPaymentOnly)
              }
              className={clsx({ "less-padding": isTreesModalExperiment })}
            />
            {isTreesModalExperiment && (
              <TreesModal
                image={CDN_ASSETS.capone.trees.confirmation}
                header={t.TREES_MODAL_HEADER}
                title={t.TREES_MODAL_TITLE}
                onClose={() => setTreeModalOpen(false)}
                subtitle={t.TREES_MODAL_SUBTITLE}
                imageLocation={t.TREES_MODAL_LOCATION}
                icon={<Icon className="trees-icon" name={IconName.TreesIcon} />}
                openModal={treeModalOpen}
                setOpenModal={setTreeModalOpen}
                modalLinkCopy={t.TREES_MODAL_CTA_TEXT}
                modalButtonCopyStartIcon={
                  <Icon className="trees-icon" name={IconName.TreesIcon} />
                }
                boldedModalLinkCopy={t.TREES_BOLDED_MODAL_CTA_TEXT}
              />
            )}
          </Box>

          {isTripPurposeXpEnabled && isExpenseManagementExperiment && (
            <Box
              className={clsx(
                "checkout-template-card-content-container",
                "trip-purpose"
              )}
            >
              <TripDescription
                setTripDescription={setTripDescription}
                isEMEligible={isEMEligible}
              />
            </Box>
          )}
        </Box>
        <Box className={clsx("checkout-template-column", "right")}>
          <Box className="checkout-template-column-right-wrapper">
            {isAgentPortal && (
              <ActionLink
                showTappableArea
                className="b2b"
                content={
                  <Box className="agent-details-modal-trigger">
                    <Icon className="info-icon" name={IconName.InfoCircle} />
                    <Typography className="trigger-label">
                      {t.DETAILS_MODAL_TRIGGER_LABEL}
                    </Typography>
                  </Box>
                }
                onClick={() => setGroundDetailsModalOpen(true)}
              />
            )}
            <Box className="car-book-right-content">
              {showPolicyBanner ? (
                <PolicyBanner isInPolicy={isInPolicy} />
              ) : null}
              <Box className="payment-break-down-container">
                <Box
                  className={clsx(
                    "checkout-template-card-content-container",
                    "payment-break-down"
                  )}
                >
                  <PriceBreakdown />

                  {isUnderAge && (
                    <Box className="underage-banner-container">
                      <NotificationBanner
                        className="underage-banner"
                        label={t.UNDER_25_WARNING}
                        severity={BannerSeverity.INFO}
                        icon={
                          <Icon
                            className="banner-icon"
                            name={IconName.InfoCircleFill}
                          />
                        }
                      />
                    </Box>
                  )}

                  {!isInPolicy && (
                    <>
                      <ApprovalReasonTextarea
                        value={approvalRequestReason ?? ""}
                        className="approval-reason"
                        onChange={handleReasonChange}
                        corporateTravel={corporateTravel}
                        productType="car"
                        limit={maxPricePerDay}
                      />
                      <ApprovalBanner
                        isApprovalRequired={
                          policies?.settings &&
                          policies.settings.isApprovalRequired
                        }
                      />
                    </>
                  )}
                  <Box className="confirm-book-button-container">
                    <Button
                      className="confirm-book-button"
                      disabled={
                        !isBookingValid ||
                        !allStepsCompleted ||
                        (!isInPolicy && !approvalRequestReason)
                      }
                      onClick={onBookCar}
                    >
                      {t.BOOK_BUTTON_TEXT}
                    </Button>
                  </Box>
                </Box>
              </Box>
              {groundDetailsModalOpen && (
                <DesktopPopupModal
                  className="agent-ground-details-modal"
                  invisibleBackdrop={false}
                  onClose={() => setGroundDetailsModalOpen(false)}
                  open={groundDetailsModalOpen}
                >
                  <CarShopDetails hideBanner hidePaymentSection />
                </DesktopPopupModal>
              )}
            </Box>

            {isCheckoutRewardsEarnBannerExperiment && (
              <Box
                className={clsx("rewards-earn-banner-container")}
                style={{ marginTop: "16px" }}
              >
                <CorpRewardsEarnBanner
                  earn={calculatedEarn}
                  earnRewardsAccount={accountForBanner}
                  funnelType={FunnelType.CAR}
                />
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};
